import { HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IFileUploadTransformers,
  NodeFileUploadResult,
  UploadData,
} from '@core/models/global-entities/file-input-upload-transformers';
import {
  SimpleResponse,
  NodeSimpleResponse,
  ListResponse,
  NodeListResponseWithCount,
  NodeListResponseWithoutCount,
  NodeListResponseWithData,
  NodeListResponseWithRows,
  ExpressResponse,
} from '@core/models/response-models/response-models';
import { isEmpty, partition } from 'lodash';
import { tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { BaseService } from './base.service';
import { saveAs } from 'file-saver';

@Injectable({
  providedIn: 'root',
})
export class BaseNodeService extends BaseService {
  constructor(public apiService: ApiService) {
    super();
  }

  toSimpleResponse<T>(response: NodeSimpleResponse<T>): SimpleResponse<T> {
    const data = new SimpleResponse<T>();
    const errResp = this.apiService.updateErrorResponse(
      response,
      'errors',
      data
    );
    if (errResp) {
      return data;
    }
    Object.assign(data, response);
    data.Result = response?.data;
    return data;
  }

  toListResponse<T>(
    response: NodeListResponseWithCount<T>,
    errorCheck = false
  ): ListResponse<T> {
    const transformed = new ListResponse<T>();
    if (errorCheck) {
      const errResp = this.apiService.updateErrorResponse(
        response,
        'errors',
        transformed
      );
      if (errResp) {
        return transformed;
      }
    }
    Object.assign(transformed, response);
    transformed.Result = response?.data && response.data.rows;
    transformed.TotalRecords = response?.data && response.data.count;
    return transformed;
  }

  toListResponseWithoutCount<T>(
    response: NodeListResponseWithoutCount<T>
  ): ListResponse<T> {
    const transformed = new ListResponse<T>();
    Object.assign(transformed, response);
    transformed.Result = response?.data;
    transformed.TotalRecords = (response?.data ?? []).length;
    return transformed;
  }

  toListResponseWithData<T>(
    response: NodeListResponseWithData<T>
  ): ListResponse<T> {
    const transformed = new ListResponse<T>();
    Object.assign(transformed, response);
    transformed.Result = response?.data?.data;
    transformed.TotalRecords = (response?.data?.data ?? []).length;
    return transformed;
  }

  toListResponseWithRows<T>(
    response: NodeListResponseWithRows<T>
  ): ListResponse<T> {
    const transformed = new ListResponse<T>();
    Object.assign(transformed, response);
    transformed.Result = response?.data?.data?.rows;
    transformed.TotalRecords =
      response?.data?.data?.count ?? (transformed.Result || []).length;
    return transformed;
  }

  toSimpleResponseWithData<T>(response): SimpleResponse<T> {
    const transformed = new SimpleResponse<T>();
    const errResp = this.apiService.updateErrorResponse(
      response,
      'errors',
      transformed
    );
    if (errResp) {
      return transformed;
    }
    Object.assign(transformed, response);
    transformed.Result = response?.data?.data;
    return transformed;
  }

  toSimpleResponseFromExpress<T>(
    response: ExpressResponse<T>
  ): SimpleResponse<T> {
    if (!response?.resp_status) {
      return this.apiService.setErrorResponse(response?.resp_code);
    }
    const data = new SimpleResponse<T>();
    Object.assign(data, response);
    data.Result = response?.resp_data;
    return data;
  }

  private uploadAttachment(url: string, formData: FormData, data?: UploadData) {
    const head = this.apiService.getDefaultHeaders();
    if (data.propertyId) {
      formData.append('propertyId', data.propertyId.toString());
    }
    if (data.entityType) {
      formData.append('entityType', data.entityType.toString());
    }
    if (data.attachmentType) {
      formData.append('attachmentType', data.attachmentType.toString());
    }
    const req = new HttpRequest('POST', url, formData, {
      reportProgress: true,
      responseType: 'json',
      headers: head,
    });

    return this.apiService.http.request<
      NodeSimpleResponse<NodeFileUploadResult>
    >(req);
  }

  uploadFilesWithProgress(
    url: string,
    formData: FormData,
    partProgress: number,
    trackProgressCb: (value: number) => void,
    data?: UploadData
  ) {
    partProgress = partProgress || 100;
    let progressTracked = 0;
    return this.uploadAttachment(url, formData, data)
      .pipe(
        tap((event) => {
          this.apiService.checkErrorMsg(event);
          if (event.type === HttpEventType.UploadProgress) {
            progressTracked += Math.round(
              (partProgress * event.loaded) / event.total
            );
            trackProgressCb(progressTracked);
          }
        })
      )
      .toPromise();
  }

  uploadMultipleFiles(
    files: File[],
    uploadHelper: IFileUploadTransformers,
    trackProgressCb: (value: number) => void,
    data?: UploadData
  ) {
    const { transformer, url } = uploadHelper;
    const partProgress = 100 / files.length;
    const formDataList = files.map((f) => transformer([f]));
    const promises = formDataList.map((formData) =>
      this.uploadFilesWithProgress(
        url,
        formData,
        partProgress,
        trackProgressCb,
        data
      )
    );

    return Promise.all(promises).then((responses) => {
      const fileUploadRes = responses
        .map((resp) => (resp instanceof HttpResponse ? resp : null))
        .map((resp, i) => ({
          file: files[i],
          response: resp,
        }));

      const [success, failed] = partition(fileUploadRes, (res) => {
        return {
          attachmentId: res?.response?.body?.data?.attachmentId,
        };
      });
      if (failed.length) {
        throw new Error('ERR_SERVER_UNKNOWN_ERR');
      }
      return success.map((res) =>
        Object.assign(res, { response: res.response.body.data })
      );
    });
  }

  downloadExcelFile<T>(
    url: string,
    req: T,
    savedFileName: string,
    trackLoading = true,
    method: 'GET' | 'POST' = 'POST'
  ) {
    const msExcelType = 'application/vnd.ms-excel';
    const openExcelType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    return new Promise((resolve, reject) => {
      this.apiService.downloadExcel(url, req, trackLoading, method).subscribe(
        (event) => {
          if (event instanceof HttpResponse) {
            if (event.status < 300 && event.status > 299) {
              console.error('Download Http Error:', event);
              reject();
              return;
            }
            const contentType = event.headers.get('content-type');
            if (contentType.indexOf('application/json') >= 0) {
              reject(event.body);
            } else if (
              contentType === msExcelType ||
              contentType === openExcelType
            ) {
              resolve(event.body);
            } else {
              console.log('UNKNOWN');
              reject(event);
            }
          }
        },
        (err) => {
          console.error('Stream Error:', err);
          reject(err);
        }
      );
    }).then((resp) => {
      const blob = new Blob([resp as BufferSource], { type: openExcelType });
      return saveAs(blob, `${savedFileName}.xlsx`);
    });
  }
}
